'use client';

import React, { useState } from 'react';
import { Heart } from 'lucide-react';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import type { PaymentIntent } from '@stripe/stripe-js';
import { getStripe } from '@/lib/stripe/client';
import { createClient } from '@/lib/supabase/client';
import WeddingNav from '@/components/WeddingNav';

const stripePromise = getStripe();

// ── Inner payment form (rendered inside <Elements>) ──────────────────────────
interface PaymentFormProps {
  clientSecret: string;
  onSuccess: (pi: PaymentIntent) => void;
  onError: (msg: string) => void;
}

function PaymentForm({ clientSecret, onSuccess, onError }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const supabase = createClient();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/honeymoon-fund`,
      },
      redirect: 'if_required',
    });

    if (error) {
      onError(error.message ?? 'Payment failed. Please try again.');
      setProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      // Confirm on backend
      const { error: confirmError } = await supabase.functions.invoke('confirm-payment', {
        body: { paymentIntentId: paymentIntent.id },
      });
      if (confirmError) {
        console.error('Backend confirm error:', confirmError);
      }
      onSuccess(paymentIntent);
    }

    setProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <PaymentElement
        options={{
          layout: 'tabs',
        }}
      />
      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full py-4 text-xs tracking-[0.3em] uppercase font-sans border border-faded-navy text-faded-navy hover:bg-faded-navy hover:text-buttermilk transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Heart size={14} strokeWidth={1.5} />
        {processing ? 'Processing…' : 'Complete Contribution'}
      </button>
    </form>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function HoneymoonFundPage() {
  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  // Step: 'form' | 'payment' | 'success'
  const [step, setStep] = useState<'form' | 'payment' | 'success'>('form');
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const supabase = createClient();

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const numAmount = parseFloat(amount);
    if (!numAmount || numAmount < 1) {
      setErrorMsg('Please enter an amount of at least £1.');
      return;
    }
    setErrorMsg('');
    setLoading(true);

    const { data, error } = await supabase.functions.invoke<{
      clientSecret?: string;
      error?: string;
    }>('create-payment-intent', {
      body: {
        amount: Math.round(numAmount * 100), // pence
        currency: 'gbp',
        contributorName: name || undefined,
        contributorEmail: email || undefined,
        message: message || undefined,
      },
    });

    setLoading(false);

    if (error || !data?.clientSecret) {
      setErrorMsg(
        (data as { error?: string })?.error ??
          error?.message ??
          'Something went wrong. Please try again.'
      );
      return;
    }

    setClientSecret(data.clientSecret);
    setStep('payment');
  };

  const handleSuccess = (_pi: PaymentIntent) => {
    setStep('success');
  };

  const handlePaymentError = (msg: string) => {
    setErrorMsg(msg);
  };

  return (
    <main className="bg-buttermilk min-h-screen">
      <WeddingNav />

      {/* Hero */}
      <section className="pt-40 pb-16 px-8 md:px-16 xl:px-24 text-center">
        <Heart size={28} strokeWidth={1} className="text-warm-sand mx-auto mb-8" />
        <div className="flex justify-center mb-6">
          <div className="relative max-w-sm w-full">
            <img
              src="/assets/images/IMG_0706-1777410023192.png"
              alt="George and Boadecea"
              className="w-full"
            />
            <span
              className="absolute bottom-2 right-2 text-white/70 font-sans"
              style={{ fontSize: '9px', letterSpacing: '0.02em' }}
            >
              The Naoshima Pumpkin, Oct 24
            </span>
          </div>
        </div>
        <div className="h-px bg-border w-16 mx-auto mb-8" />
        <p className="font-sans text-sm text-stone leading-relaxed mb-4 max-w-lg mx-auto">
          Truly, having you with us in Dartmouth on our wedding day is everything we could ask for. No gift is expected or required.
        </p>
        <p className="font-sans text-sm text-stone leading-relaxed max-w-lg mx-auto">
          However, if you would like to contribute to our honeymoon adventures, we would be deeply grateful. Since getting engaged in Japan we are dreaming of returning and have our sights set on a new province, embracing nature, new activities and of course the art and culture
        </p>
        <p className="font-sans text-sm text-stone leading-relaxed max-w-lg mx-auto mt-4">
          Arigato gozaimasu!
        </p>
      </section>

      {/* Fund section */}
      <section className="pb-32 px-8 md:px-16 xl:px-24">
        <div className="max-w-md mx-auto">

          {/* ── Success state ── */}
          {step === 'success' && (
            <div className="border border-border p-10 bg-off-white text-center">
              <Heart size={24} strokeWidth={1} className="text-warm-sand mx-auto mb-6" />
              <p className="font-serif text-xl text-faded-navy mb-3">Thank you so much.</p>
              <p className="font-sans text-sm text-stone leading-relaxed">
                Your generosity will make our honeymoon all the more special.
              </p>
            </div>
          )}

          {/* ── Stripe payment form ── */}
          {step === 'payment' && clientSecret && (
            <div className="space-y-5">
              <button
                type="button"
                onClick={() => { setStep('form'); setErrorMsg(''); }}
                className="font-sans text-xs text-stone/60 hover:text-stone transition-colors duration-200 tracking-wide"
              >
                ← Back
              </button>
              <p className="font-sans text-xs tracking-[0.2em] uppercase text-stone mb-4">
                Secure Payment · £{amount}
              </p>
              {errorMsg && (
                <p className="font-sans text-xs text-red-500 mb-2">{errorMsg}</p>
              )}
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: {
                    theme: 'stripe',
                    variables: {
                      colorPrimary: '#3d4a5c',
                      colorBackground: '#faf9f6',
                      colorText: '#3d4a5c',
                      fontFamily: 'inherit',
                      borderRadius: '0px',
                    },
                  },
                }}
              >
                <PaymentForm
                  clientSecret={clientSecret}
                  onSuccess={handleSuccess}
                  onError={handlePaymentError}
                />
              </Elements>
            </div>
          )}

          {/* ── Contribution details form ── */}
          {step === 'form' && (
            <form onSubmit={handleFormSubmit} className="space-y-5">
              {/* Amount */}
              <div>
                <label className="block font-sans text-xs tracking-[0.2em] uppercase text-stone mb-2">
                  Amount
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 font-sans text-sm text-stone">£</span>
                  <input
                    type="number"
                    min="1"
                    step="any"
                    placeholder="Enter an amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    required
                    className="w-full border border-border bg-transparent py-3 pl-8 pr-4 text-sm font-sans text-faded-navy outline-none focus:border-warm-sand transition-colors duration-300 placeholder:text-stone/40"
                  />
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block font-sans text-xs tracking-[0.2em] uppercase text-stone mb-2">
                  Your name <span className="normal-case tracking-normal text-stone/50">(optional)</span>
                </label>
                <input
                  type="text"
                  placeholder="How should we thank you?"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full border border-border bg-transparent py-3 px-4 text-sm font-sans text-faded-navy outline-none focus:border-warm-sand transition-colors duration-300 placeholder:text-stone/40"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block font-sans text-xs tracking-[0.2em] uppercase text-stone mb-2">
                  Email <span className="normal-case tracking-normal text-stone/50">(optional)</span>
                </label>
                <input
                  type="email"
                  placeholder="For your receipt"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-border bg-transparent py-3 px-4 text-sm font-sans text-faded-navy outline-none focus:border-warm-sand transition-colors duration-300 placeholder:text-stone/40"
                />
              </div>

              {/* Message */}
              <div>
                <label className="block font-sans text-xs tracking-[0.2em] uppercase text-stone mb-2">
                  Leave a message <span className="normal-case tracking-normal text-stone/50">(optional)</span>
                </label>
                <textarea
                  placeholder="Write a little note for us…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  className="w-full border border-border bg-transparent py-3 px-4 text-sm font-sans text-faded-navy outline-none focus:border-warm-sand transition-colors duration-300 placeholder:text-stone/40 resize-none"
                />
              </div>

              {errorMsg && (
                <p className="font-sans text-xs text-red-500">{errorMsg}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 text-xs tracking-[0.3em] uppercase font-sans border border-faded-navy text-faded-navy hover:bg-faded-navy hover:text-buttermilk transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Heart size={14} strokeWidth={1.5} />
                {loading ? 'Setting up payment…' : 'Contribute to the Honeymoon Fund'}
              </button>

              <p className="text-xs font-sans text-stone/50 text-center">
                Secure payment powered by Stripe
              </p>
            </form>
          )}

        </div>
      </section>
    </main>
  );
}
